import React, { useCallback, useEffect, useRef, useState } from 'react';

const BEFORE_SRC = '/demo/kinvera-before.jpg';
const AFTER_CANDIDATES = ['/demo/kinvera-after.jpg', '/demo/kinvera-after.png'];

function probeImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

type Props = {
  fallback: React.ReactNode;
  /** When false, hide the three supporting points under the slider (default true). */
  showSupportingPoints?: boolean;
};

export const MarketingBeforeAfter: React.FC<Props> = ({ fallback, showSupportingPoints = true }) => {
  const [ready, setReady] = useState(false);
  const [afterSrc, setAfterSrc] = useState<string | null>(null);
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);
  const userTookOver = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const beforeOk = await probeImage(BEFORE_SRC);
      if (!beforeOk || cancelled) {
        if (!cancelled) setReady(false);
        return;
      }
      for (const candidate of AFTER_CANDIDATES) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await probeImage(candidate);
        if (cancelled) return;
        if (ok) {
          setAfterSrc(candidate);
          setReady(true);
          return;
        }
      }
      if (!cancelled) setReady(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reduced) {
      setPosition(50);
      return;
    }

    setPosition(30);
    const start = performance.now();
    const duration = 2800;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const frame = (now: number) => {
      if (userTookOver.current) return;
      const t = Math.min(1, (now - start) / duration);
      let pos: number;
      if (t < 0.55) {
        pos = 30 + (70 - 30) * ease(t / 0.55);
      } else {
        pos = 70 + (50 - 70) * ease((t - 0.55) / 0.45);
      }
      setPosition(Math.round(pos));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        setPosition(50);
      }
    };

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [ready]);

  const updateFromClientX = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition(Math.round((x / rect.width) * 100));
  }, []);

  const takeOver = useCallback(() => {
    userTookOver.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    takeOver();
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => updateFromClientX(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, updateFromClientX]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    takeOver();
    setPosition((prev) => {
      const delta = e.key === 'ArrowLeft' ? -3 : 3;
      return Math.max(0, Math.min(100, prev + delta));
    });
  };

  if (!ready || !afterSrc) {
    return <>{fallback}</>;
  }

  return (
    <div className="kin-mkt-compare">
      <div
        ref={containerRef}
        className="kin-mkt-compare-stage"
        onPointerDown={onPointerDown}
        role="slider"
        aria-label="Before and after restoration comparison"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={position}
        aria-valuetext={`${position}% restored view`}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <img src={BEFORE_SRC} alt="Original family photograph before restoration" className="kin-mkt-compare-img" draggable={false} />
        <div className="kin-mkt-compare-after" style={{ clipPath: `inset(0 0 0 ${position}%)` }}>
          <img src={afterSrc} alt="Same photograph after Kinvera restoration" className="kin-mkt-compare-img" draggable={false} />
        </div>

        <div className="kin-mkt-compare-divider" style={{ left: `${position}%` }} aria-hidden="true">
          <span className="kin-mkt-compare-handle" />
        </div>

        <span className="kin-mkt-compare-label kin-mkt-compare-label-before">Original</span>
        <span className="kin-mkt-compare-label kin-mkt-compare-label-after">Restored</span>
      </div>

      {showSupportingPoints && (
        <ul className="kin-mkt-compare-points">
          <li>
            <strong>Restore damage</strong>
            <span>Heal glare, creases, scratches, and fading with restraint.</span>
          </li>
          <li>
            <strong>Preserve identity</strong>
            <span>Keep the same faces — expression, age, and likeness.</span>
          </li>
          <li>
            <strong>Keep the photograph</strong>
            <span>Maintain medium, composition, and historical character.</span>
          </li>
        </ul>
      )}
    </div>
  );
};
